import { FEVER_PITCH, NOTES as N } from './audioNotes';

interface Note {
  freq: number;  // Hz  (0 = rest)
  dur: number;   // seconds
  gain?: number;
  type?: OscillatorType;
}

type MusicMode = 'menu' | 'anima' | 'shop' | 'game' | 'fever' | 'shellcrush' | 'goldrush' | 'starburst' | 'pemdas' | 'pemdas_fever' | 'shellcrush_fever' | 'goldrush_fever';

interface Song {
  mel: Note[];
  bass: Note[];
}

const MUSIC: Record<MusicMode, Song> = {
  menu: {
    mel: [
      { freq: N.C4, dur: 0.5 }, { freq: N.E4, dur: 0.5 }, { freq: N.G4, dur: 0.5 }, { freq: N.B4, dur: 0.5 },
      { freq: N.C5, dur: 0.5 }, { freq: N.B4, dur: 0.5 }, { freq: N.G4, dur: 0.5 }, { freq: N.E4, dur: 0.5 }
    ],
    bass: [
      { freq: N.C2, dur: 1 }, { freq: N.G2, dur: 1 }, { freq: N.C2, dur: 1 }, { freq: N.G2, dur: 1 }
    ]
  },
  game: {
    mel: [
      { freq: N.C5, dur: 0.25 }, { freq: N.D5, dur: 0.25 }, { freq: N.E5, dur: 0.25 }, { freq: N.G5, dur: 0.25 },
      { freq: N.A5, dur: 0.25 }, { freq: N.G5, dur: 0.25 }, { freq: N.E5, dur: 0.25 }, { freq: N.D5, dur: 0.25 }
    ],
    bass: [
      { freq: N.C3, dur: 0.5 }, { freq: N.G3, dur: 0.5 }, { freq: N.C3, dur: 0.5 }, { freq: N.G3, dur: 0.5 }
    ]
  },
  fever: {
    mel: [
      { freq: N.C6, dur: 0.15 }, { freq: N.G5, dur: 0.15 }, { freq: N.E5, dur: 0.15 }, { freq: N.G5, dur: 0.15 }
    ],
    bass: [
      { freq: N.C4, dur: 0.3 }, { freq: N.G3, dur: 0.3 }
    ]
  },
  shellcrush: { mel: [], bass: [] },
  goldrush: { mel: [], bass: [] },
  starburst: { mel: [], bass: [] },
  pemdas: { mel: [], bass: [] },
  anima: { mel: [], bass: [] },
  shop: { mel: [], bass: [] },
  pemdas_fever: { mel: [], bass: [] },
  shellcrush_fever: { mel: [], bass: [] },
  goldrush_fever: { mel: [], bass: [] },
};

// Properly link game mode themes to the main 'game' song
MUSIC.shellcrush = MUSIC.game;
MUSIC.goldrush = MUSIC.game;
MUSIC.starburst = MUSIC.game;
MUSIC.pemdas = MUSIC.game;
MUSIC.anima = MUSIC.game;
MUSIC.shop = MUSIC.game;
MUSIC.pemdas_fever = MUSIC.fever;
MUSIC.shellcrush_fever = MUSIC.fever;
MUSIC.goldrush_fever = MUSIC.fever;


class AudioEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private bgGain: GainNode | null = null;

  private schedulerTimer: any = null;
  private currentMode: MusicMode | null = null;
  private melIdx = 0;
  private bassIdx = 0;
  private melNextTime = 0;
  private bassNextTime = 0;

  private _sfxEnabled = true;
  private _musicEnabled = true;
  private _sfxVol = 0.7;
  private _musicVol = 0.28;

  // Sound Buffers Cache
  private buffers: Record<string, AudioBuffer> = {};

  // Real Menu BGM source node
  private activeBgmSource: AudioBufferSourceNode | null = null;
  private activeBgmGain: GainNode | null = null;

  constructor() {
    this._sfxEnabled = localStorage.getItem('nr_sfx') !== 'false';
    this._musicEnabled = localStorage.getItem('nr_music') !== 'false';
    this._sfxVol = parseFloat(localStorage.getItem('nr_sfx_vol') ?? '0.7');
    this._musicVol = parseFloat(localStorage.getItem('nr_music_vol') ?? '0.28');
    
    // Preload sounds
    this.preloadSounds();

    // Auto-resume AudioContext on first user gesture to defeat browser autoplay policies
    const resumeOnGesture = () => {
      const ctx = this.getCtx();
      if (ctx.state === 'suspended') {
        ctx.resume().then(() => {
          cleanupListeners();
        });
      } else {
        cleanupListeners();
      }
    };

    const cleanupListeners = () => {
      window.removeEventListener('click', resumeOnGesture);
      window.removeEventListener('touchend', resumeOnGesture);
      window.removeEventListener('keydown', resumeOnGesture);
    };

    window.addEventListener('click', resumeOnGesture);
    window.addEventListener('touchend', resumeOnGesture);
    window.addEventListener('keydown', resumeOnGesture);
  }

  private async preloadSounds() {
    const soundFiles = [
      // UI
      { name: 'not_enough', path: '/assets/Sound/UI/not enough.wav' },
      { name: 'game_over', path: '/assets/Sound/UI/game over.wav' },
      { name: 'claim', path: '/assets/Sound/UI/claim.wav' },
      { name: 'ready', path: '/assets/Sound/UI/ready.wav' },
      { name: 'go', path: '/assets/Sound/UI/go.wav' },
      
      // Menu BGMs (MP3 format)
      { name: 'home_theme', path: '/assets/Sound/BGM/home_theme.mp3' },
      { name: 'anima_theme', path: '/assets/Sound/BGM/anima_theme.mp3' },
      { name: 'shop_theme', path: '/assets/Sound/BGM/shop_theme.mp3' },

      // Gameplay Base Loops (File A)
      { name: 'cloudmach_theme', path: '/assets/Sound/BGM/cloudmach_theme.mp3' },
      { name: 'shellcrush_theme', path: '/assets/Sound/BGM/shellcrush_theme.mp3' },
      { name: 'goldrush_theme', path: '/assets/Sound/BGM/goldrush_theme.mp3' },
      { name: 'starburst_theme', path: '/assets/Sound/BGM/starburst_theme.mp3' },

      // Gameplay Fever Extensions (File B)
      { name: 'cloudmach_fever_theme', path: '/assets/Sound/BGM/cloudmach_fever_theme.mp3' },
      { name: 'shellcrush_fever_theme', path: '/assets/Sound/BGM/shellcrush_fever_theme.mp3' },
      { name: 'goldrush_fever_theme', path: '/assets/Sound/BGM/goldrush_fever_theme.mp3' },
      
      // Mode Hits
        { name: 'cloudmach_hit', path: '/assets/Sound/cloud mach/cloud hit.mp3' },
        { name: 'goldrush_hit', path: '/assets/Sound/gold rush/gold rush hit.wav' },
        { name: 'shellcrush_hit', path: '/assets/Sound/shellcrush/shell crush hit.wav' },
        { name: 'shellcrush_hit_9', path: '/assets/Sound/shellcrush/shell crush 9 hit.wav' },
        { name: 'shellcrush_hit_fever_9', path: '/assets/Sound/shellcrush/shell crush fever 9 hit.wav' },
        { name: 'starburst_hit', path: '/assets/Sound/star burst/star burst hit.wav' },
      
      // Mode Fever Starts
      { name: 'cloudmach_fever', path: '/assets/Sound/cloud mach/cloud fever.wav' },
      { name: 'goldrush_fever', path: '/assets/Sound/gold rush/gold rush fever.wav' },
      { name: 'shellcrush_fever', path: '/assets/Sound/shellcrush/shell crush fever.wav' },
      
      // Generated SFX
      { name: 'click', path: '/assets/Sound/Generated/click.wav' },
      { name: 'wrong_sfx', path: '/assets/Sound/Generated/wrong.wav' },
      { name: 'countdown', path: '/assets/Sound/Generated/countdown.wav' },
      { name: 'go_fallback', path: '/assets/Sound/Generated/go.wav' },
      { name: 'pop', path: '/assets/Sound/Generated/pop.wav' },
      { name: 'shimmer', path: '/assets/Sound/Generated/shimmer.wav' },
      { name: 'crunch', path: '/assets/Sound/Generated/crunch.wav' },
      { name: 'level_up', path: '/assets/Sound/Generated/level_up.wav' },
      { name: 'box_open', path: '/assets/Sound/Generated/box_open.wav' },
      { name: 'tick', path: '/assets/Sound/Generated/tick.wav' },
    ];

    // Load all sounds in parallel for a 1000% speed boost!
    await Promise.all(
      soundFiles.map(async (file) => {
        try {
          const response = await fetch(file.path);
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          const arrayBuffer = await response.arrayBuffer();
          const ctx = this.getCtx();
          const decoded = await ctx.decodeAudioData(arrayBuffer);
          this.buffers[file.name] = decoded;
          
          // Seamless transition when the currently requested BGM finishes loading in the background
          this.handleLoadedBgm(file.name);
        } catch (err) {
          console.warn(`Failed to load sound: ${file.path}`, err);
        }
      })
    );
  }

  private handleLoadedBgm(bufferKey: string) {
    if (!this.currentMode) return;
    
    // Check if the loaded buffer corresponds to the current mode
    let targetModeForBuffer: MusicMode | null = null;
    if (bufferKey === 'home_theme') targetModeForBuffer = 'menu';
    else if (bufferKey === 'anima_theme') targetModeForBuffer = 'anima';
    else if (bufferKey === 'shop_theme') targetModeForBuffer = 'shop';
    else if (bufferKey === 'cloudmach_theme') targetModeForBuffer = 'pemdas';
    else if (bufferKey === 'shellcrush_theme') targetModeForBuffer = 'shellcrush';
    else if (bufferKey === 'goldrush_theme') targetModeForBuffer = 'goldrush';
    else if (bufferKey === 'starburst_theme') targetModeForBuffer = 'starburst';
    else if (bufferKey === 'cloudmach_fever_theme') targetModeForBuffer = 'pemdas_fever';
    else if (bufferKey === 'shellcrush_fever_theme') targetModeForBuffer = 'shellcrush_fever';
    else if (bufferKey === 'goldrush_fever_theme') targetModeForBuffer = 'goldrush_fever';

    if (targetModeForBuffer === this.currentMode) {
      // If we are currently running the retro scheduler fallback, swap it with the real track instantly!
      if (this.schedulerTimer) {
        const m = this.currentMode;
        this.currentMode = null; // Reset so startMusic is not ignored
        this.startMusic(m);
      }
    }
  }

  private getCtx(): AudioContext {
    if (!this.ctx) {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      this.ctx = new AudioContextClass({ latencyHint: 'interactive' });

      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = 1;
      this.masterGain.connect(this.ctx.destination);

      this.sfxGain = this.ctx.createGain();
      this.sfxGain.gain.value = this._sfxVol;
      this.sfxGain.connect(this.masterGain);

      this.bgGain = this.ctx.createGain();
      this.bgGain.gain.value = this._musicVol;
      this.bgGain.connect(this.masterGain);
    }

    if (this.ctx.state === 'suspended') this.ctx.resume();
    return this.ctx;
  }

  private tone(
    freq: number,
    dur: number,
    gain: number,
    type: OscillatorType = 'sine',
    startAt?: number,
    dest?: AudioNode,
    pitchSlide?: number,
  ) {
    const ctx = this.getCtx();
    const t = startAt ?? ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    if (pitchSlide !== undefined) {
      osc.frequency.exponentialRampToValueAtTime(pitchSlide, t + dur);
    }
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);

    osc.connect(g);
    g.connect(dest ?? this.sfxGain ?? ctx.destination);
    osc.start(t);
    osc.stop(t + dur + 0.01);
  }

  private playBuffer(name: string, gain: number = 1.0, playbackRate: number = 1.0) {
    if (!this._sfxEnabled || !this.buffers[name]) return;
    const ctx = this.getCtx();
    const source = ctx.createBufferSource();
    source.buffer = this.buffers[name];
    source.playbackRate.value = playbackRate;
    
    const g = ctx.createGain();
    g.gain.value = gain;
    
    source.connect(g);
    g.connect(this.sfxGain ?? ctx.destination);
    source.start(0);

    // Cleanup
    source.onended = () => {
      source.disconnect();
      g.disconnect();
    };
  }

  // ── SFX ───────────────────────────────────────────────────────────────────

  /** Correct answer — plays specific hit sound if available, else fallback to tone */
  playCorrect(feverStack = 0, mode?: 'pemdas' | 'shellcrush' | 'starburst' | 'goldrush', hasNine = false, isFever = false) {
    if (!this._sfxEnabled) return;
    
    if (mode === 'shellcrush') {
      if (isFever && hasNine && this.buffers.shellcrush_hit_fever_9) {
        this.playBuffer('shellcrush_hit_fever_9');
      } else if (!isFever && hasNine && this.buffers.shellcrush_hit_9) {
        this.playBuffer('shellcrush_hit_9');
      } else if (this.buffers.shellcrush_hit) {
        this.playBuffer('shellcrush_hit');
      } else {
        if (this.buffers.pop) this.playBuffer('pop');
        else {
          const freq = FEVER_PITCH[Math.min(feverStack, 9)];
          this.tone(freq, 0.12, 0.45, 'sine');
          this.tone(freq * 1.5, 0.08, 0.18, 'sine');
          this.tone(freq * 2, 0.04, 0.12, 'triangle');
        }
      }
    } else if (mode === 'starburst' && this.buffers.starburst_hit) {
      this.playBuffer('starburst_hit');
    } else if (mode === 'goldrush' && this.buffers.goldrush_hit) {
      this.playBuffer('goldrush_hit', 1.0, isFever ? 1.35 : 1.0);
    } else if ((mode === 'pemdas' || !mode) && this.buffers.cloudmach_hit) {
      this.playBuffer('cloudmach_hit');
    } else {
      // Fallback: check if we have mode-specific generated hits
      if (mode === 'starburst' && this.buffers.shimmer) this.playBuffer('shimmer');
      else if (mode === 'goldrush' && this.buffers.crunch) this.playBuffer('crunch', 1.0, isFever ? 1.35 : 1.0);
      else {
        // Fallback/Programmatic tone
        let freq = FEVER_PITCH[Math.min(feverStack, 9)];
        if (isFever && mode === 'goldrush') {
          freq = freq * 1.35;
        }
        this.tone(freq, 0.12, 0.45, 'sine');
        this.tone(freq * 1.5, 0.08, 0.18, 'sine');
        this.tone(freq * 2, 0.04, 0.12, 'triangle');
      }
    }
  }

  /** Game over — sad trombone or dramatic end */
  playGameOver() {
    if (!this._sfxEnabled) return;
    if (this.buffers.game_over) {
      this.playBuffer('game_over');
    } else {
      // Fallback: descending buzz
      this.playWrong();
    }
  }

  /** Successful claim — jingle for rewards/purchases */
  playClaim() {
    if (!this._sfxEnabled) return;
    if (this.buffers.claim) {
      this.playBuffer('claim');
    } else {
      // Fallback: cheerful ascending chime
      this.playDailyGift();
    }
  }

  /** Fever mode starting — dramatic build-up */
  playFeverStart(mode: 'pemdas' | 'shellcrush' | 'goldrush') {
    if (!this._sfxEnabled) return;
    const bufferKey = mode === 'pemdas' ? 'cloudmach_fever' : `${mode}_fever`;
    if (this.buffers[bufferKey]) {
      this.playBuffer(bufferKey);
    } else {
      // Fallback
      this.playFeverDing();
    }
  }

  /** Generic fever sound trigger */
  playFever() {
    this.playFeverDing();
  }


  /** Satisfying completion sound (Legacy support) */
  playPurchaseDone() {
    this.playClaim();
  }

  /** Lacking currency or resources */
  playNotEnough() {
    if (!this._sfxEnabled) return;
    if (this.buffers.not_enough) {
      this.playBuffer('not_enough');
    } else {
      this.playWrong();
    }
  }

  playWrong() {
    if (!this._sfxEnabled) return;
    if (this.buffers.wrong_sfx) {
      this.playBuffer('wrong_sfx', 0.6);
    } else {
      const ctx = this.getCtx();
      const t = ctx.currentTime;
      this.tone(200, 0.30, 0.30, 'sawtooth', t, undefined, 70);
      this.tone(80, 0.20, 0.20, 'sine', t);
    }
  }

  playThud() {
    if (!this._sfxEnabled) return;
    if (this.buffers.crunch) {
      this.playBuffer('crunch', 0.5);
    } else {
      const ctx = this.getCtx();
      const t = ctx.currentTime;
      this.tone(60, 0.25, 0.45, 'sine', t);
      this.tone(120, 0.15, 0.30, 'triangle', t);
    }
  }

  playDing() {
    if (!this._sfxEnabled) return;
    if (this.buffers.click) {
      this.playBuffer('click', 0.8);
    } else {
      const ctx = this.getCtx();
      const t = ctx.currentTime;
      this.tone(880, 0.15, 0.35, 'sine', t);
      this.tone(1760, 0.10, 0.15, 'sine', t);
    }
  }

  playFeverDing() {
    if (!this._sfxEnabled) return;
    if (this.buffers.go) {
      this.playBuffer('go', 0.7);
    } else {
      const ctx = this.getCtx();
      const t = ctx.currentTime;
      this.tone(300, 0.40, 0.35, 'sine', t, undefined, 1200);
      [N.C5, N.E5, N.G5, N.C6].forEach((f, i) => {
        this.tone(f, 0.14, 0.30, 'sine', t + 0.35 + i * 0.07);
      });
    }
  }

  playOpenBox() {
    if (!this._sfxEnabled) return;
    if (this.buffers.box_open) {
      this.playBuffer('box_open', 0.7);
    } else {
      const ctx = this.getCtx();
      const t = ctx.currentTime;
      [N.C5, N.E5, N.G5, N.C6, N.E6].forEach((f, i) => {
        this.tone(f, 0.40, 0.28 - i * 0.04, 'sine', t + i * 0.09);
        this.tone(f * 2, 0.20, 0.08, 'sine', t + i * 0.09);
      });
    }
  }

  playDailyGift() {
    if (!this._sfxEnabled) return;
    if (this.buffers.level_up) {
      this.playBuffer('level_up', 0.7);
    } else {
      const ctx = this.getCtx();
      const t = ctx.currentTime;
      const melody = [N.C5, N.E5, N.G5, N.A5, N.C6];
      melody.forEach((f, i) => {
        this.tone(f, 0.30, 0.32, 'sine', t + i * 0.10);
      });
      this.tone(N.C6 * 2, 0.50, 0.12, 'sine', t + 0.40);
    }
  }

  playEvolution() {
    if (!this._sfxEnabled) return;
    if (this.buffers.level_up) {
      this.playBuffer('level_up', 0.85);
    } else {
      const ctx = this.getCtx();
      const t = ctx.currentTime;
      const jingle = [N.C4, N.F4, N.A4, N.C5, N.F5];
      jingle.forEach((f, i) => {
        this.tone(f, 0.25, 0.35, 'sine', t + i * 0.08);
        this.tone(f * 2, 0.15, 0.1, 'triangle', t + i * 0.08);
      });
      this.tone(N.F5 * 2, 0.60, 0.15, 'sine', t + 0.35);
    }
  }

  playHighScore() {
    if (!this._sfxEnabled) return;
    if (this.buffers.level_up) {
      this.playBuffer('level_up', 0.8);
    } else {
      const ctx = this.getCtx();
      const t = ctx.currentTime;
      const fanfare = [
        [N.C4, 0.10], [N.G4, 0.10], [N.C5, 0.10],
        [N.E5, 0.12], [N.G5, 0.12],
        [N.C6, 0.55],
      ] as [number, number][];
      let offset = 0;
      fanfare.forEach(([f, d]) => {
        this.tone(f, d + 0.05, 0.38, 'sine', t + offset);
        this.tone(f * 1.25, d + 0.05, 0.12, 'triangle', t + offset);
        offset += d + 0.01;
      });
    }
  }

   playSound(type: 'click' | 'success' | 'error' | 'not_enough' | 'tick' | 'countdown' | 'ready' | 'go') {
    if (!this._sfxEnabled) return;
    switch (type) {
      case 'click':
        if (this.buffers.click) this.playBuffer('click', 0.4);
        else {
          const t = this.getCtx().currentTime;
          this.tone(1200, 0.03, 0.15, 'sine', t);
          this.tone(600, 0.05, 0.1, 'triangle', t + 0.01);
        }
        break;
      case 'success':
        this.playDing();
        break;
      case 'error':
        this.playWrong();
        break;
      case 'not_enough':
        this.playNotEnough();
        break;
      case 'tick':
        if (this.buffers.tick) this.playBuffer('tick', 0.3);
        break;
      case 'countdown':
      case 'ready':
        if (this.buffers.ready) this.playBuffer('ready', 0.8);
        else if (this.buffers.countdown) this.playBuffer('countdown', 0.6);
        break;
      case 'go':
        if (this.buffers.go) this.playBuffer('go', 0.8);
        else if (this.buffers.go_fallback) this.playBuffer('go_fallback', 0.8);
        break;
    }
  }


  // ── Background music scheduler ────────────────────────────────────────────
  private readonly LOOK_AHEAD = 0.2; 
  private readonly TICK = 50;         


  private scheduleNote(note: Note, at: number, dest: AudioNode) {
    if (note.freq === 0) return; 
    this.tone(note.freq, note.dur * 0.85, note.gain ?? 0.14, note.type ?? 'sine', at, dest, undefined);
  }

  private runScheduler() {
    if (!this.currentMode || !this.ctx || !this.bgGain) return;
    const { mel, bass } = MUSIC[this.currentMode];
    const now = this.ctx.currentTime;
    const limit = now + this.LOOK_AHEAD;

    while (this.melNextTime < limit) {
      const note = mel[this.melIdx % mel.length];
      this.scheduleNote(note, this.melNextTime, this.bgGain);
      this.melNextTime += note.dur;
      this.melIdx++;
    }
    while (this.bassNextTime < limit) {
      const note = bass[this.bassIdx % bass.length];
      this.scheduleNote(note, this.bassNextTime, this.bgGain);
      this.bassNextTime += note.dur;
      this.bassIdx++;
    }
  }

  startMusic(mode: MusicMode) {
    if (!this._musicEnabled) return;
    if (this.currentMode === mode) return; 
    
    // Check if we are switching between standard gameplay and fever counterpart
    // If so, use a tight 150ms crossfade to make the transition punchy!
    const isStandardGame = ['pemdas', 'shellcrush', 'goldrush'].includes(this.currentMode || '');
    const isFeverGame = ['pemdas_fever', 'shellcrush_fever', 'goldrush_fever'].includes(this.currentMode || '');
    const isTargetStandard = ['pemdas', 'shellcrush', 'goldrush'].includes(mode);
    const isTargetFever = ['pemdas_fever', 'shellcrush_fever', 'goldrush_fever'].includes(mode);
    
    const useTightCrossfade = (isStandardGame && isTargetFever) || (isFeverGame && isTargetStandard);
    const fadeMs = useTightCrossfade ? 150 : 400;

    const ctx = this.getCtx();
    this.currentMode = mode;

    // Check if we have preloaded BGM buffers
    let bgmKey: string | null = null;
    if (mode === 'menu') bgmKey = 'home_theme';
    else if (mode === 'anima') bgmKey = 'anima_theme';
    else if (mode === 'shop') bgmKey = 'shop_theme';
    else if (mode === 'pemdas') bgmKey = 'cloudmach_theme';
    else if (mode === 'shellcrush') bgmKey = 'shellcrush_theme';
    else if (mode === 'goldrush') bgmKey = 'goldrush_theme';
    else if (mode === 'starburst') bgmKey = 'starburst_theme';
    else if (mode === 'pemdas_fever') bgmKey = 'cloudmach_fever_theme';
    else if (mode === 'shellcrush_fever') bgmKey = 'shellcrush_fever_theme';
    else if (mode === 'goldrush_fever') bgmKey = 'goldrush_fever_theme';

    if (bgmKey && this.buffers[bgmKey]) {
      try {
        if (this.schedulerTimer) {
          clearInterval(this.schedulerTimer);
          this.schedulerTimer = null;
        }

        // Capture previous active source and private gain node to fade out independently
        const oldSource = this.activeBgmSource;
        const oldGain = this.activeBgmGain;

        // Restore master background music gain in case it was previously ramped down to 0
        if (this.bgGain) {
          this.bgGain.gain.setValueAtTime(this._musicVol, ctx.currentTime);
        }

        // Instantiate new private gain node connected to master background gain node
        const newGain = ctx.createGain();
        newGain.connect(this.bgGain ?? ctx.destination);

        const source = ctx.createBufferSource();
        source.buffer = this.buffers[bgmKey];
        source.loop = true;

        // Apply programmatic loop points to clip Suno pops and artifacts
        if (bgmKey === 'home_theme') {
          source.loopStart = 0;
          source.loopEnd = 27.8;
        } else if (bgmKey === 'anima_theme') {
          source.loopStart = 0;
          source.loopEnd = 30.5;
        } else if (bgmKey === 'shop_theme') {
          source.loopStart = 0;
          source.loopEnd = 25.1;
        }

        // Apply BPM drift compensation for standard Goldrush theme (Option A: 128 BPM -> 130 BPM)
        if (bgmKey === 'goldrush_theme') {
          source.playbackRate.value = 1.015625; // scaling playback rate by 1.5625% locks standard onto the 130 BPM Fever grid
        }

        // Set up volume fade in on private GainNode
        newGain.gain.setValueAtTime(0, ctx.currentTime);
        newGain.gain.linearRampToValueAtTime(1.0, ctx.currentTime + (fadeMs / 1000));

        source.connect(newGain);
        source.start(0);

        this.activeBgmSource = source;
        this.activeBgmGain = newGain;

        // Smoothly fade out and stop previous BGM track
        if (oldSource && oldGain) {
          const t = ctx.currentTime;
          oldGain.gain.setValueAtTime(oldGain.gain.value, t);
          oldGain.gain.linearRampToValueAtTime(0, t + fadeMs / 1000);
          setTimeout(() => {
            try {
              oldSource.stop();
              oldSource.disconnect();
              oldGain.disconnect();
            } catch (e) {}
          }, fadeMs);
        }

        return; // High-fidelity playback succeeded! Skip procedural synth scheduler.
      } catch (err) {
        console.warn(`Failed to play high-fidelity BGM (${bgmKey}), falling back to retro synth:`, err);
      }
    }

    // Procedural Retro Synth Fallback Scheduler
    this.stopMusic(fadeMs);
    this.currentMode = mode; // reset after stopMusic clears it
    
    this.melIdx = 0;
    this.bassIdx = 0;
    this.melNextTime = ctx.currentTime + 0.05;
    this.bassNextTime = ctx.currentTime + 0.05;

    if (this.bgGain) {
      this.bgGain.gain.setValueAtTime(0, ctx.currentTime);
      this.bgGain.gain.linearRampToValueAtTime(this._musicVol, ctx.currentTime + (fadeMs / 1000));
    }

    this.schedulerTimer = setInterval(() => this.runScheduler(), this.TICK);
  }

  stopMusic(fadeMs = 600) {
    if (this.schedulerTimer) {
      clearInterval(this.schedulerTimer);
      this.schedulerTimer = null;
    }

    const ctx = this.ctx ? this.ctx : null;

    if (this.activeBgmSource) {
      const activeSrc = this.activeBgmSource;
      const activeGain = this.activeBgmGain;
      
      this.activeBgmSource = null;
      this.activeBgmGain = null;

      if (activeGain && activeSrc && ctx) {
        const t = ctx.currentTime;
        activeGain.gain.setValueAtTime(activeGain.gain.value, t);
        activeGain.gain.linearRampToValueAtTime(0, t + fadeMs / 1000);

        // Stop the source node after the gain fade completes
        setTimeout(() => {
          try {
            activeSrc.stop();
            activeSrc.disconnect();
            activeGain.disconnect();
          } catch (e) {}
        }, fadeMs);
      } else {
        try {
          activeSrc.stop();
          activeSrc.disconnect();
        } catch (e) {}
      }
    } else if (this.bgGain && ctx) {
      const t = ctx.currentTime;
      this.bgGain.gain.setValueAtTime(this.bgGain.gain.value, t);
      this.bgGain.gain.linearRampToValueAtTime(0, t + fadeMs / 1000);
    }

    this.currentMode = null;
  }

  get sfxEnabled() { return this._sfxEnabled; }
  get musicEnabled() { return this._musicEnabled; }
  get sfxVol() { return this._sfxVol; }
  get musicVol() { return this._musicVol; }

  setSfxEnabled(v: boolean) {
    this._sfxEnabled = v;
    localStorage.setItem('nr_sfx', String(v));
  }

  setMusicEnabled(v: boolean) {
    this._musicEnabled = v;
    localStorage.setItem('nr_music', String(v));
    if (!v) {
      const previousMode = this.currentMode;
      this.stopMusic();
      this.currentMode = previousMode; // Remember what was playing!
    }
    else if (this.currentMode) {
      const m = this.currentMode;
      this.currentMode = null;
      this.startMusic(m);
    }
  }

  suspendAudio() {
    if (this.ctx && this.ctx.state === 'running') {
      this.ctx.suspend().catch(e => console.warn('Audio suspend failed', e));
    }
  }

  resumeAudio() {
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume().catch(e => console.warn('Audio resume failed', e));
    }
  }

  setSfxVol(v: number) {
    this._sfxVol = v;
    localStorage.setItem('nr_sfx_vol', String(v));
    if (this.sfxGain) this.sfxGain.gain.value = v;
  }

  setMusicVol(v: number) {
    this._musicVol = v;
    localStorage.setItem('nr_music_vol', String(v));
    if (this.bgGain) this.bgGain.gain.value = v;
  }
}

export const audioEngine = new AudioEngine();
